import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useIsLearner, useIsPerson } from "@domain/currentProfile/hooks";
import { usePersonCourses } from "@domain/lms/course/hooks";
import { Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { Secondary } from "@ui/components/containers/textBlocks";
import { CourseThumbnail } from "@ui/components/course/courseThumbnail";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Stack } from "@ui/components/layout";
import { desktop, gap, mobile } from "@ui/styles/utils";

interface ProfileCoursesListProps extends React.HTMLAttributes<HTMLDivElement> {
	user: Person;
	isTeacher?: boolean;
	organizationName?: string;
}

export const ProfileCoursesList: React.FC<ProfileCoursesListProps> = ({
	user,
	isTeacher,
	organizationName,
	...htmlProps
}) => {
	const isOrganization = user.type === "COMPANY" || user.type === "INSTITUTE";
	const { result: courses, fetchMore, loading, totalElements, moreLoading } = usePersonCourses(user.id, isOrganization);
	const isAuthor = useIsPerson(user);
	const isLearner = useIsLearner();

	return totalElements === 0 && !loading ? (
		<Stack gap={10}>
			<Secondary>{I18n.t("profile.courses.empty", { user: fullName(user) })}</Secondary>
			{isTeacher && !isLearner && isOrganization && organizationName ? (
				<Link to={Path.Lms.Editor("new")}>
					<PrimaryButton>{I18n.t(`organization.course.incentive`, { institute: organizationName })}</PrimaryButton>
				</Link>
			) : null}
		</Stack>
	) : (
		<InfiniteScroll onEndReached={fetchMore} loading={loading || moreLoading}>
			<Courses {...htmlProps}>
				{courses.map((course) =>
					course ? <CourseThumbnail key={course.id} course={course} displayStatus={isAuthor} /> : undefined
				)}
				{loading || (moreLoading && <ClipLoader />)}
			</Courses>
		</InfiniteScroll>
	);
};

const Courses = styled.div`
	@media ${desktop} {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-gap: 20px;
	}
	@media ${mobile} {
		${gap(20)};
	}
`;
